import { useState, useEffect, useCallback } from 'react';
import { api, getCategories } from '../api';
import { styles } from '../styles';
import { BackIcon, Spinner } from '../components/Icons';

function SearchIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="8" />
      <path d="M21 21l-4.35-4.35" />
    </svg>
  );
}

function ClearIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <path d="M15 9l-6 6M9 9l6 6" />
    </svg>
  );
}

const PRESETS = [
  { key: 'all', label: 'All Time' },
  { key: '6m', label: '6 Months' },
  { key: '12m', label: '12 Months' },
  { key: 'year', label: 'Year' },
];

function getDateRange(preset, selectedYear) {
  const now = new Date();
  
  if (preset === 'all') return { startDate: null, endDate: null };
  
  if (preset === 'year') {
    return {
      startDate: new Date(selectedYear, 0, 1),
      endDate: new Date(selectedYear, 11, 31, 23, 59, 59),
    };
  }
  
  const months = preset === '6m' ? 6 : 12;
  return {
    startDate: new Date(now.getFullYear(), now.getMonth() - months + 1, 1),
    endDate: new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59),
  };
}

export default function ReceiptsScreen({ onBack, onReceiptSelect }) {
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [userId, setUserId] = useState('');
  const [expenseType, setExpenseType] = useState('');
  const [preset, setPreset] = useState('all');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [categories, setCategories] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  useEffect(() => {
    getCategories().then(setCategories).catch(() => {});
  }, []);

  const fetchReceipts = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (categoryId) params.set('category_id', categoryId);
    if (userId) params.set('user_id', userId);
    if (expenseType) params.set('expense_type', expenseType);
    
    const { startDate, endDate } = getDateRange(preset, selectedYear);
    if (startDate) params.set('start_date', startDate.toISOString());
    if (endDate) params.set('end_date', endDate.toISOString());
    
    params.set('limit', '100');

    try {
      const data = await api.get(`/receipts?${params}`);
      setReceipts(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [search, categoryId, userId, expenseType, preset, selectedYear]);

  useEffect(() => {
    const debounce = setTimeout(fetchReceipts, 300);
    return () => clearTimeout(debounce);
  }, [fetchReceipts]);

  return (
    <div style={styles.screen}>
      <header style={styles.header}>
        <button style={styles.backBtn} onClick={onBack}>
          <BackIcon />
        </button>
        <h1 style={styles.headerTitle}>Receipts</h1>
        <div style={{ width: 20 }} />
      </header>

      {/* Search Input */}
      <div style={searchStyles.searchContainer}>
        <div style={searchStyles.searchIcon}>
          <SearchIcon />
        </div>
        <input
          style={searchStyles.searchInput}
          type="text"
          placeholder="Search merchant..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <button style={searchStyles.clearBtn} onClick={() => setSearch('')}>
            <ClearIcon />
          </button>
        )}
      </div>

      {/* Date Presets */}
      <div style={searchStyles.presets}>
        {PRESETS.map(p => (
          <button
            key={p.key}
            style={{
              ...searchStyles.presetBtn,
              ...(preset === p.key ? searchStyles.presetActive : {}),
            }}
            onClick={() => setPreset(p.key)}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Year picker when "Year" preset is selected */}
      {preset === 'year' && (
        <div style={searchStyles.yearPicker}>
          {years.map(y => (
            <button
              key={y}
              style={{
                ...searchStyles.yearBtn,
                ...(selectedYear === y ? searchStyles.yearActive : {}),
              }}
              onClick={() => setSelectedYear(y)}
            >
              {y}
            </button>
          ))}
        </div>
      )}

      {/* Filters */}
      <div style={searchStyles.filters}>
        <select
          style={searchStyles.filterSelect}
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
        >
          <option value="">All Categories</option>
          {categories.filter(c => c.is_active).map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <select
          style={searchStyles.filterSelect}
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
        >
          <option value="">All Users</option>
          <option value="00000000-0000-0000-0000-000000000001">LCG</option>
          <option value="00000000-0000-0000-0000-000000000003">SGW</option>
        </select>
      </div>
      <div style={searchStyles.filters}>
        <select
          style={searchStyles.filterSelect}
          value={expenseType}
          onChange={(e) => setExpenseType(e.target.value)}
        >
          <option value="">All Types</option>
          <option value="personal">Personal</option>
          <option value="corporate">Corporate</option>
        </select>
      </div>

      {/* Results */}
      {loading ? (
        <div style={styles.centered}><Spinner /></div>
      ) : receipts.length === 0 ? (
        <p style={searchStyles.empty}>
          {search ? `No receipts matching "${search}"` : 'No receipts found'}
        </p>
      ) : (
        <div style={searchStyles.list}>
          <p style={searchStyles.resultCount}>{receipts.length} receipt{receipts.length !== 1 ? 's' : ''}</p>
          {receipts.map(r => (
            <button
              key={r.id}
              style={searchStyles.item}
              onClick={() => onReceiptSelect(r)}
            >
              <div style={searchStyles.itemLeft}>
                <span style={searchStyles.merchant}>
                  {r.merchant_name || 'Unknown'}
                  {r.expense_type === 'corporate' && (
                    <span style={searchStyles.corpBadge}>CORP</span>
                  )}
                </span>
                <span style={searchStyles.meta}>
                  {r.category?.name || 'Uncategorized'}
                  {' · '}
                  {r.transaction_date
                    ? new Date(r.transaction_date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })
                    : 'No date'}
                </span>
              </div>
              <span style={searchStyles.amount}>${Number(r.grand_total).toFixed(2)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const searchStyles = {
  presets: {
    display: 'flex',
    gap: 8,
    marginBottom: 12,
  },
  presetBtn: {
    flex: 1,
    padding: '8px 10px',
    fontSize: 13,
    fontWeight: 500,
    backgroundColor: 'white',
    color: '#64748b',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    cursor: 'pointer',
  },
  presetActive: {
    backgroundColor: '#6366f1',
    color: 'white',
    borderColor: '#6366f1',
  },
  yearPicker: {
    display: 'flex',
    gap: 8,
    marginBottom: 12,
    overflowX: 'auto',
  },
  yearBtn: {
    padding: '6px 14px',
    fontSize: 13,
    fontWeight: 500,
    backgroundColor: 'white',
    color: '#64748b',
    border: '1px solid #e2e8f0',
    borderRadius: 6,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  yearActive: {
    backgroundColor: '#f1f5f9',
    color: '#1e293b',
    borderColor: '#94a3b8',
  },
  searchContainer: {
    position: 'relative',
    marginBottom: 12,
  },
  searchIcon: {
    position: 'absolute',
    left: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#94a3b8',
  },
  searchInput: {
    width: '100%',
    padding: '12px 40px 12px 40px',
    fontSize: 15,
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    backgroundColor: 'white',
    outline: 'none',
    boxSizing: 'border-box',
  },
  clearBtn: {
    position: 'absolute',
    right: 8,
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    color: '#94a3b8',
    cursor: 'pointer',
    padding: 4,
  },
  filters: {
    display: 'flex',
    gap: 10,
    marginBottom: 16,
  },
  filterSelect: {
    flex: 1,
    padding: '10px 12px',
    fontSize: 14,
    borderRadius: 10,
    border: '1px solid #e2e8f0',
    backgroundColor: 'white',
    color: '#1e293b',
  },
  resultCount: {
    fontSize: 13,
    color: '#64748b',
    marginBottom: 12,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
  },
  item: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: 'white',
    padding: 14,
    borderRadius: 12,
    border: '1px solid #e2e8f0',
    marginBottom: 8,
    cursor: 'pointer',
    textAlign: 'left',
    width: '100%',
  },
  itemLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  merchant: {
    fontSize: 15,
    fontWeight: 500,
    color: '#1e293b',
  },
  meta: {
    fontSize: 12,
    color: '#64748b',
  },
  amount: {
    fontSize: 15,
    fontWeight: 600,
    color: '#1e293b',
  },
  empty: {
    textAlign: 'center',
    color: '#94a3b8',
    padding: 40,
  },
  corpBadge: {
    fontSize: 9,
    backgroundColor: '#dbeafe',
    color: '#1d4ed8',
    padding: '1px 5px',
    borderRadius: 4,
    fontWeight: 600,
    marginLeft: 6,
    verticalAlign: 'middle',
  },
};